/**
 * Global Navi 内部の URL 解決ヘルパー。
 *
 * 自基盤 (`selfPlatform`) 向けは相対パス、他基盤向けは絶対 URL（cross-domain）として返す。
 * プロジェクト指定のクエリパラメータは **ターゲット host の規約** に従う:
 * - karte-io: `?project=<KARTE projectId>`（navigation-v5 / getAdminProjectUrl と同じ規約）
 * - qant:     `?pid=<QANT projectId>`
 *
 * 例外として `NaviTarget.projectIdIn === 'path'` のリンクは `/p/<projectId>` をパスに前置する
 * (KARTE 本体の機能ページ。KARTE 現行ナビの `getProjectUrl` に相当)。
 *
 * projectId は platform ごとに異なるため `projectIds` から ターゲット platform の id を引く。
 * ターゲット platform の id が無い場合（他基盤分の取得失敗等）はパラメータを付けず、
 * 遷移先アプリのデフォルト project 解決に任せる。
 *
 * 注意: karte-io の `/organization/**` は `?project=` だけでは足りず `?org=` も要る。KARTE 基盤は
 * project から組織を逆引きせず cookie `krt_current_org_id` で補完するため、org が無いと直前に別組織を
 * 見ていた利用者がアクセス拒否になる (righttouch/systems#17963)。ここでは org を自動付与しないので、
 * `/organization/**` へリンクする箇所は `resolveProjectSettingsHref` のように個別に org を足すこと。
 */

import type { Platform } from '../contract/index.js';
import type { MenuItem, ProductMenu } from '../types/menuModel.js';
import type { NaviHost, NaviTarget } from '../url/resolveNaviUrl.js';
import { resolveNaviUrl } from '../url/resolveNaviUrl.js';

const PLATFORM_TO_HOST = {
  qant: 'qant',
  karte: 'karte-io',
} as const satisfies { [K in Platform]: NaviHost };

const HOST_TO_PLATFORM = {
  qant: 'qant',
  'karte-io': 'karte',
} as const satisfies { [K in NaviHost]: Platform };

/** ターゲット host ごとのプロジェクト指定クエリパラメータ名 */
const HOST_PROJECT_PARAM = {
  qant: 'pid',
  'karte-io': 'project',
} as const satisfies { [K in NaviHost]: string };

/**
 * QANT 基盤 → KARTE 基盤への遷移 URL に付与する自動 SSO トリガーパラメータ。
 * KARTE の login ハンドラが未認証 + このパラメータで OIDC を即時開始する
 * (docs/logout-problem/logout-strategy-02.md §5.1)。
 */
export const AUTO_SSO_PARAM = { name: 'sso', value: 'qant' } as const;

const appendProjectParam = (href: string, host: NaviHost, projectId: string | undefined, crossDomain: boolean): string => {
  if (!projectId) return href;
  // ホスト側で querystring を持つ場合に備えて URL を経由して安全に追加する。
  const base = crossDomain ? href : `${typeof window === 'undefined' ? 'http://__internal__' : window.location.origin}${href}`;
  const url = new URL(base);
  url.searchParams.set(HOST_PROJECT_PARAM[host], projectId);
  if (crossDomain) return url.toString();
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * `/p/<projectId>` をパスに前置する (`NaviTarget.projectIdIn === 'path'`)。
 * KARTE 本体の機能ページはこの形式でしかプロジェクトを解釈しない。
 */
const prependProjectPath = (href: string, projectId: string | undefined, crossDomain: boolean): string => {
  if (!projectId) return href;
  const prefix = `/p/${encodeURIComponent(projectId)}`;
  if (!crossDomain) return `${prefix}${href}`;
  const url = new URL(href);
  url.pathname = `${prefix}${url.pathname}`;
  return url.toString();
};

export type HrefBuilder = (target: NaviTarget) => { href: string; crossDomain: boolean };

export type HrefBuilderParams = {
  selfPlatform: Platform;
  /**
   * 自基盤の projectId (V1: GlobalNaviData.project.id / V2: current.projectIds[selfPlatform])。
   * current 未確定時は省略可（プロジェクト指定クエリを付けない）。
   */
  projectId?: string;
  /** platform ごとの projectId。selfPlatform 分は projectId から補完される */
  projectIds?: { qant?: string; karte?: string };
  /**
   * platform ごとの cross-domain 遷移先 origin (GlobalNaviData.platformOrigins, scheme 込み)。
   * 指定が無い platform は prod ドメイン (NAVI_DOMAINS) にフォールバックする。
   */
  platformOrigins?: { qant?: string; karte?: string };
};

export const createHrefBuilder = ({ selfPlatform, projectId, projectIds, platformOrigins }: HrefBuilderParams): HrefBuilder => {
  const hostApp = PLATFORM_TO_HOST[selfPlatform];
  const ids: { qant?: string; karte?: string } = { ...(projectIds ?? {}) };
  if (projectId) {
    ids[selfPlatform] ??= projectId;
  }
  return (target) => {
    const targetPlatform = HOST_TO_PLATFORM[target.host];
    const resolved = resolveNaviUrl(target, hostApp, platformOrigins?.[targetPlatform]);
    const targetProjectId = target.skipProjectParam ? undefined : ids[targetPlatform];
    let href =
      target.projectIdIn === 'path'
        ? prependProjectPath(resolved.href, targetProjectId, resolved.crossDomain)
        : appendProjectParam(resolved.href, target.host, targetProjectId, resolved.crossDomain);
    // プロダクト単位で絞らず karte-io 向け全リンク (KARTE 本体リンク含む) に付与する。
    // KARTE 側の分岐は未認証時にしか作用しないため、受け口のないページでは無視されるだけで無害。
    if (selfPlatform === 'qant' && target.host === 'karte-io' && resolved.crossDomain) {
      const url = new URL(href);
      url.searchParams.set(AUTO_SSO_PARAM.name, AUTO_SSO_PARAM.value);
      href = url.toString();
    }
    return { href, crossDomain: resolved.crossDomain };
  };
};

/**
 * MenuItem から表示用 href を生成。path が無い場合は '#'。
 */
export const resolveMenuHref = (menu: MenuItem, builder: HrefBuilder): string => {
  if (!menu.path) return '#';
  return builder(menu.path).href;
};

/**
 * 指定 product のランディング先 (href / crossDomain) を返す。外部リンク、明示された landingPath、
 * 先頭の path を持つ menu の順に採用する。product 切替時の遷移先として使う。
 * crossDomain の判定は呼び出し側で「自基盤ホストの URL として組み立て直してよいか」の判断に使う
 * （builder の base URL と unrelated な origin を混ぜて壊れた URL を作らないため）。
 */
export const resolveProductLandingTarget = (
  product: ProductMenu,
  builder: HrefBuilder,
): { href: string; crossDomain: boolean } | undefined => {
  // 外部リンクプロダクト (product.link) はサブメニューを持たないので link.path を landing に使う。
  if (product.link) return builder(product.link.path);
  if (product.landingPath) return builder(product.landingPath);
  for (const group of product.menuGroups) {
    for (const menu of group.menus) {
      if (menu.path) return builder(menu.path);
    }
  }
  return undefined;
};

/**
 * 指定 product のランディング href を返す。詳細は `resolveProductLandingTarget` を参照。
 */
export const resolveProductLandingHref = (product: ProductMenu, builder: HrefBuilder): string | undefined =>
  resolveProductLandingTarget(product, builder)?.href;

/** プロジェクト切替では表示中の基盤に残る。L1 の landingPath が別基盤なら自基盤のメニューを使う。 */
export const resolveProjectSwitchLandingHref = (product: ProductMenu, builder: HrefBuilder): string | undefined => {
  const landing = resolveProductLandingTarget(product, builder);
  if (landing && !landing.crossDomain) return landing.href;
  for (const group of product.menuGroups) {
    for (const menu of group.menus) {
      if (!menu.path) continue;
      const target = builder(menu.path);
      if (!target.crossDomain) return target.href;
    }
  }
  return undefined;
};
